import React, { Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import { useSelector } from "react-redux";
import Redirect from "../modules/guard/redirect";
import Layout from "../app/components/layout";
import { LazyLoadSpinner } from "@/modules/workflows/atoms/loadingSpinner";
import { RootState } from "../app/store";
import { ViteMicroApps } from "@/app/common/types";
import {
  __federation_method_getRemote,
  __federation_method_setRemote,
  // @ts-expect-error: The "__federation__" module is generated at build time and does not have TypeScript type definitions. This import is required for module federation runtime functionality.
} from "__federation__";

const ChatPage = React.lazy(() =>
  import("../modules/chat/pages/chatPage").then((module) => ({
    default: module.ChatPage,
  }))
);

const WorkFlowsPage = React.lazy(() =>
  import("../modules/workflows/pages/workFlowsPage").then((module) => ({
    default: module.WorkFlowsPage,
  }))
);

const DashboardPage = React.lazy(() =>
  import("../modules/dashboard/pages/dashboardPage").then((module) => ({
    default: module.DashboardPage,
  }))
);

const AgentsPage = React.lazy(() =>
  import("../modules/workflows/pages/agentsPage").then((module) => ({
    default: module.AgentsPage,
  }))
);

const TasksPage = React.lazy(() =>
  import("../modules/workflows/pages/tasksPage").then((module) => ({
    default: module.TasksPage,
  }))
);

const CrewsPage = React.lazy(() =>
  import("../modules/workflows/pages/crewsPage").then((module) => ({
    default: module.CrewsPage,
  }))
);

const ToolsPage = React.lazy(() =>
  import("../modules/workflows/pages/toolsPage").then((module) => ({
    default: module.ToolsPage,
  }))
);

const ChainOfCustodyPage = React.lazy(() =>
  import("../modules/chainOfCustody/pages/chainOfCustodyPage").then((module) => ({
    default: module.ChainOfCustodyPage,
  }))
);

const remotes = (
  (JSON.parse(import.meta.env.VITE_MICRO_APPS ?? "[]") ?? []) as ViteMicroApps[]
).map((el) => {
  return {
    name: el.app,
    href: el.href,
    remoteEntryUrl: el.remoteEntryUrl,
  };
});

//dynamic remote component factory 
const createDynamicRemoteComponent = (
  remoteName: string,
  remoteEntryUrl: string,
  modulePath: string = "./App"
) => {
  return React.lazy(() => {
    __federation_method_setRemote(remoteName, {
      url: () => Promise.resolve(remoteEntryUrl),
      format: "esm",
      from: "vite",
    });

    return __federation_method_getRemote(remoteName, modulePath);
  });
};

// passing user state to micro apps
const MicroFrontendWrapper = ({
  Component,
  ...otherProps
}: {
  Component: React.ComponentType<any>;
}) => {
  const userState = useSelector((state: RootState) => state.user);
  const env = import.meta.env.VITE_CHAT_URL;
  return <Component userState={userState} env={env} {...otherProps} />;
};

const DynamicComponents: {
  [name: string]: React.LazyExoticComponent<React.ComponentType<any>>;
} = remotes.reduce((prev, curr) => {
  return {
    ...prev,
    [curr.name]: createDynamicRemoteComponent(curr.name, curr.remoteEntryUrl),
  };
}, {});

const remoteRoutes = remotes.map((el) => {
  const Component = DynamicComponents[el.name];

  return {
    path: `/${el.href}`,
    element: (
      <Suspense fallback={<LazyLoadSpinner />}>
        <MicroFrontendWrapper Component={Component} />
      </Suspense>
    ),
  };
});

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LazyLoadSpinner />}>
            <ChatPage />
          </Suspense>
        ),
      },

      ...remoteRoutes,
      {
        path: "workflows",
        element: (
          <Suspense fallback={<LazyLoadSpinner />}>
            <WorkFlowsPage />
          </Suspense>
        ),
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<LazyLoadSpinner />}>
                <CrewsPage />
              </Suspense>
            ),
          },
          {
            path: "agents",
            element: (
              <Suspense fallback={<LazyLoadSpinner />}>
                <AgentsPage />
              </Suspense>
            )
          },
          {
            path: "tasks",
            element: (
              <Suspense fallback={<LazyLoadSpinner />}>
                <TasksPage />
              </Suspense>
            )
          },
          {
            path: "tools",
            element: (
              <Suspense fallback={<LazyLoadSpinner />}>
                <ToolsPage />
              </Suspense>
            )
          },
        ]
      },
      {
        path: "dashboard",
        element: (
          <Suspense fallback={<LazyLoadSpinner />}>
            <DashboardPage />
          </Suspense>
        ),
      },
      {
        path: "chain-of-custody",
        element: (
          <Suspense fallback={<LazyLoadSpinner/>}>
            <ChainOfCustodyPage/>
          </Suspense>
        ),
      },
      {
        path: "redirect",
        element: (
          <Redirect/>
        )
      }
    ]
  },
]);

export default router;
